/**
 * Static workshop content configuration — replaces 11 DB table queries per page load.
 *
 * Keyed by series_slug. Each entry contains the six actively-rendered content
 * sections plus a boolean gate for the THINGS_YOULL_BUILD section.
 *
 * To update content for a workshop: find the matching series slug below and
 * edit that section. Changes here are reflected on the live site after deployment.
 * Each series is clearly labelled so changes to one cannot affect another.
 *
 * howDayWorks, requirements, testimonials, and nextSteps were fetched from the DB
 * but never rendered in any component. They are omitted here intentionally.
 */

import { KIDS_FAQS } from "./kids-camp-meta";

// ─── Item shape interfaces ────────────────────────────────────────────────────

export interface WorkshopAgendaItem {
  timeStart: string;
  timeEnd: string;
  title: string;
  description: string | null;
  sessionType: string | null;
}

export interface WorkshopLearningOutcome {
  icon: string | null;
  headline: string;
  description: string | null;
}

export interface WorkshopInclusion {
  title: string;
}

export interface WorkshopInstructor {
  name: string;
  title: string | null;
  headshotUrl: string | null;
  bio: string | null;
  accomplishments: string[] | null;
  linkedinUrl: string | null;
  /** One-line personal aside rendered under the bio with a "FUN FACT" label.
   *  Optional — only the Cowork instructors set it. */
  funFact?: string;
  displayOrder?: number;
}

export interface WorkshopFaqItem {
  question: string;
  answer: string;
  // When set, the substring `answerLink.text` within `answer` is rendered as a
  // link to `answerLink.href`.
  answerLink?: { text: string; href: string };
}

export interface WorkshopBrandLogo {
  imageUrl: string;
  altText: string | null;
  linkUrl: string | null;
}

export interface WorkshopAudience {
  /** Heading above the two columns. The default was written for the AI-First
   *  Leader workshop, the only series that used to carry an audience block;
   *  any other series should say its own thing here. */
  heading?: string;
  /** "This is for you if..." bullet list */
  forItems: string[];
  /** "It's not for..." bullet list */
  notForItems: string[];
  /** Optional closing line under the two columns */
  closingNote?: string;
}

export interface WorkshopContentEntry {
  agendaItems: WorkshopAgendaItem[];
  learningOutcomes: WorkshopLearningOutcome[];
  inclusions: WorkshopInclusion[];
  instructors: WorkshopInstructor[];
  faqItems: WorkshopFaqItem[];
  brandLogos: WorkshopBrandLogo[];
  /** When set, renders the "Who Should Attend" section below What You'll Learn */
  audience?: WorkshopAudience;
  /** Gates the THINGS_YOULL_BUILD section — only true for claude-code-for-beginners-workshop */
  showThingsYoullBuild: boolean;
  /** Short title shown in the mobile sticky top bar (e.g. "Master Claude Cowork") */
  stickyBarTitle?: string;
  /** Override for the logo bar eyebrow headline. Defaults to the global "Over 450,000 students..." copy. */
  logoBarEyebrow?: string;
  /** Override for the "What you'll learn/do in ___" heading suffix (defaults to event.title) */
  learnHeadlineSuffix?: string;
  /** Hero copy override. Without it the hero shows event.title and
   *  event.shortDescription; kicker sits above the headline so the page still
   *  names the workshop when the headline is a pitch. */
  hero?: {
    kicker?: string;
    headline?: string;
    subtitle?: string;
    proofLines?: Array<{ label: string; text: string }>;
  };
}

// ─── Helper ───────────────────────────────────────────────────────────────────

const workshopContent: Record<string, WorkshopContentEntry> = {

  // ═══════════════════════════════════════════════════════════════════════════
  // CLAUDE CODE FOR KIDS — JSON-LD ONLY
  // The kids page never renders EventDetailPage; this entry exists so the
  // Event schema gets performer + FAQPage (GSC warns when performer is
  // absent). Page copy authority: client/src/pages/kids/kids-content.ts.
  // ═══════════════════════════════════════════════════════════════════════════
  "claude-code-for-kids": {
    agendaItems: [],
    learningOutcomes: [],
    inclusions: [],
    // Mirrors the roster the kids page actually renders (Isaac, Nechama,
    // Esther) — this list feeds the Event JSON-LD `performer` nodes, which
    // previously advertised Patrick, who is not on the camp page.
    instructors: [
      {
        name: "Isaac Rudansky",
        title: "Founder, AdVenture Media",
        headshotUrl: "/assets/headshots/headshot_isaac_rudansky_new.webp",
        bio: "Isaac has taught over 450,000 students online and leads the Claude workshop series taken by professionals from Forbes, Nasdaq, and AMC Networks.",
        accomplishments: null,
        linkedinUrl: null,
      },
      {
        name: "Nechama Teigman",
        title: "Director of AI Strategy",
        headshotUrl: "/images/headshots/preferred/headshot_nechama_teigman.webp",
        bio: "Nechama builds the AI agents our own team runs on every day and leads our Advanced Claude Code workshop.",
        accomplishments: null,
        linkedinUrl: null,
      },
      {
        name: "Esther Nadoff",
        title: "Design Lead, AdVenture Media",
        headshotUrl: "/images/headshots/preferred/headshot_esti_nadoff.webp",
        bio: "Esther leads design at AdVenture and built our Claude for Design workshop from the ground up.",
        accomplishments: null,
        linkedinUrl: null,
      },
    ],
    // One source of truth: the page renders KIDS_FAQS and the SSR FAQPage
    // JSON-LD is built from this same list (a 4-item copy here had already
    // forked from the page's questions).
    faqItems: KIDS_FAQS.map((f) => ({ question: f.q, answer: f.a })),
    brandLogos: [],
    showThingsYoullBuild: false,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CLAUDE COWORK FOR BEGINNERS WORKSHOP
  // ═══════════════════════════════════════════════════════════════════════════
  "claude-cowork-for-beginners-workshop": {
    agendaItems: [
      {
        timeStart: "10:00 AM",
        timeEnd: "10:30 AM",
        title: "Welcome + Claude Cowork Tour",
        description:
          "We orient the room, tour the desktop app, and lock in your setup so nobody falls behind.",
        sessionType: null,
      },
      {
        timeStart: "10:30 AM",
        timeEnd: "11:00 AM",
        title: "Generating Ideas With AdVenture's Custom Agent",
        description:
          "Use AdVenture's purpose-built agent to identify three specific build ideas and ready-to-run prompts tailored to your business.",
        sessionType: null,
      },
      {
        timeStart: "11:00 AM",
        timeEnd: "11:30 AM",
        title: "Connectors, Skills, and App Integrations",
        description:
          "Step through live integrations — email, calendar, your daily tools — then unlock Claude's power with skills add-ons.",
        sessionType: null,
      },
      {
        timeStart: "11:30 AM",
        timeEnd: "12:00 PM",
        title: "First Hands-On Build: Create Your First Automation",
        description:
          "Using the connectors and skills you just configured, build and run your first real automation from scratch.",
        sessionType: null,
      },
      {
        timeStart: "12:00 PM",
        timeEnd: "12:15 PM",
        title: "Break",
        description: null,
        sessionType: "break",
      },
      {
        timeStart: "12:15 PM",
        timeEnd: "12:45 PM",
        title: "Custom Skills and Recurring Automations With Cron Triggers",
        description:
          "Design a reusable custom skill, then wire it to a cron trigger so your automation runs on its own schedule.",
        sessionType: null,
      },
      {
        timeStart: "12:45 PM",
        timeEnd: "1:15 PM",
        title: "Dashboards and Working With Real Data",
        description:
          "Pull in live data, build an interactive analytics dashboard, and learn to generate professional documents like decks and PDFs on demand.",
        sessionType: null,
      },
      {
        timeStart: "1:15 PM",
        timeEnd: "1:30 PM",
        title: "Q&A + Next Steps",
        description:
          "Open floor for questions, and a clear roadmap for what to build the moment you leave.",
        sessionType: "qa",
      },
    ],
    learningOutcomes: [
      {
        icon: "Monitor",
        headline: "Tour the Claude Cowork Desktop App",
        description:
          "Explore model options, token management, core settings, and the customizations that matter most.",
      },
      {
        icon: "Link",
        headline: "Connect Your Apps and Power Up With Skills",
        description:
          "Walk through live integrations — email, calendar, and the tools you use daily — then supercharge Claude with skills add-ons.",
      },
      {
        icon: "Bot",
        headline: "Build Your First Autonomous Agent",
        description:
          "Using connectors and skills, build an agent that reads local files, searches the web, and executes a specific set of instructions.",
      },
      {
        icon: "Layers",
        headline: "Create a Custom Skill From Scratch",
        description:
          "Use context history and refinement loops to design and save a reusable skill — one of the most powerful features in Claude Cowork.",
      },
      {
        icon: "BarChart2",
        headline: "Build a Live Analytics Dashboard",
        description:
          "Pull in live data, generate interactive charts, and set the work to recur automatically — one build, endless reuse.",
      },
      {
        icon: "FolderOpen",
        headline: "Organize Your Work With Projects",
        description:
          "Master file management, context scoping, and custom instructions to keep every project focused and fast.",
      },
      {
        icon: "FileText",
        headline: "Master the CLAUDE.md File System",
        description:
          "Learn to configure the context Claude holds about you and your project at every step — the single highest-leverage habit in Cowork.",
      },
      {
        icon: "Sparkles",
        headline: "Prompt Like the Top 1% of Claude Users",
        description:
          "A five-step process and copy-paste prompts for transforming the quality and consistency of every Claude Cowork output.",
      },
    ],
    inclusions: [
      { title: "Live on Google Meet—stream from anywhere." },
      { title: "Small cohorts guarantee personal support" },
      { title: "Lifetime recording access" },
      { title: "7 days of personal email support" },
      { title: "Ready-to-use prompt templates" },
      { title: "Free access to the Claude Cowork Recipe Generator" },
      { title: "Free access to a custom market research agent" },
    ],
    instructors: [
      {
        name: "Isaac Rudansky",
        title: "Founder, AdVenture Media",
        headshotUrl: "/images/instructors/isaac-clay-racket-cream.webp",
        bio: "Isaac has trained over 450,000 students and helped Forbes, Asurion, Hanes, Nasdaq, and AMC Networks integrate AI. Direct, practical, jargon-free: real skills you can use the same day.",
        funFact: "Took up tennis at 34. Convinced everyone assumes he's been playing since he was 5.",
        accomplishments: [
          "450,000+ students trained",
          "Founded AdVenture Media",
          "CEO Modern Marketing Institute",
          "Google Premier Partner",
        ],
        linkedinUrl: null,
        displayOrder: 0,
      },
      {
        name: "Patrick Gilbert",
        title: "Best-Selling Author & CEO",
        headshotUrl: "/images/instructors/patrick-clay-books-cream.webp",
        bio: 'Best-selling author of "Join or Die: Digital Advertising in the Age of Automation." Patrick trains Google in automated bidding and has taught AI tools to thousands of professionals.',
        funFact: "Can't stop writing books. Two #1 bestsellers and counting.",
        accomplishments: [
          "Best-selling author of Join or Die",
          "Trains Google in automated bidding & ML",
          "Trained 5,000+ professionals on AI tools",
        ],
        linkedinUrl: null,
        displayOrder: 1,
      },
      {
        name: "Esti Nadoff",
        title: "Director of Education",
        headshotUrl: "/images/instructors/esti-clay-art-cream.webp",
        bio: "A senior strategist, Esti leads AdVenture Media's training curriculum and instructor team, running our education programs on the same playbook we use for our biggest client engagements.",
        funFact: "A closet artist. No, seriously: her closet is an actual art studio.",
        accomplishments: [
          "Leads AdVenture's training curriculum",
          "Senior strategist across client education programs",
          "Runs the Forbes · Hanes · Nasdaq playbook internally",
        ],
        linkedinUrl: null,
        displayOrder: 2,
      },
    ],
    faqItems: [
      {
        question: "What exactly is Claude Cowork?",
        answer:
          "Cowork is the autonomous agent mode inside the Claude Desktop app. Instead of responding to prompts one at a time, you give Claude a complex task and it works on it independently — planning, breaking it into subtasks, executing in an isolated environment, and delivering results. It can run for extended periods, generate professional documents, and connect to 38+ workplace tools.",
      },
      {
        question: "What's the difference between Claude Cowork and Claude Code?",
        answer:
          "Claude Cowork is built for knowledge workers and business professionals — it's a no-code, natural-language interface for building automations, connecting apps, and running agents inside the Claude Desktop app. Claude Code is a developer-facing tool for writing, editing, and shipping software code from the terminal. If you're not a developer, Cowork is the right workshop. If you write code professionally, Code is worth a look too.",
      },
      {
        question: "Do I need any technical or coding experience?",
        answer:
          "No. Cowork is designed for knowledge workers, not developers. You work in plain English — describe what you want done, and Claude handles the execution. If you can write an email, you can use Cowork.",
      },
      {
        question: "Do I need to have attended any previous workshop?",
        answer:
          "No prior workshop is required. We teach everything from scratch. If you've never used Claude before, you'll be up and running within the first 20 minutes.",
      },
      {
        question: "What tools can I connect Claude to?",
        answer:
          "Cowork supports 38+ connectors including Slack, Google Drive, Gmail, Google Calendar, Notion, GitHub, Jira, Salesforce, HubSpot, Snowflake, Linear, and more. Each connector takes under a minute to set up and uses your existing account permissions.",
      },
      {
        question: "What will I actually build during the workshop?",
        answer:
          "You'll set up at least one autonomous Cowork agent, create a scheduled recurring task, connect Claude to one or more of your real tools, and generate professional documents (spreadsheets, reports, or presentations) from real data. Most attendees leave with a working automation they use the next day.",
      },
      {
        question: "What subscription do I need?",
        answer:
          "Cowork features are available on Claude Pro ($20/mo) or Max ($100/mo) subscriptions. That said, many attendees choose to use the free plan and watch along with Isaac as he builds automations live — then decide to upgrade to Pro after the workshop once they've seen what's possible. Either way, you'll get full value from the session.",
      },
      {
        question: "Will it be recorded?",
        answer:
          "Yes. All registered attendees receive the full recording within 48 hours of the workshop ending. You also get access to the session notes, full transcript, and the slide deck.",
      },
      {
        question: "What's the refund policy?",
        answer:
          "Yes. If you attend and aren't satisfied, email us within 24 hours of the workshop ending and we'll issue a full refund, no questions asked.",
      },
      {
        question: "Can I send my team to the workshop?",
        answer:
          "Absolutely. Team tickets (3+ seats) are available at a discounted per-seat rate — select 'Team (3+)' on the checkout page. For custom quotes on teams of 10 or more, or to inquire about private workshops, reach out to Clara Rockwell directly at clara@adventureppc.com.",
      },
      {
        question: "Do you offer private team training?",
        answer:
          "Yes. We offer custom in-house and virtual trainings tailored to your team's tools, workflows, and goals. Visit our team training page to learn more about what's included and to submit an inquiry.",
        answerLink: { text: "team training page", href: "/ai-training-for-teams" },
      },
      {
        question: "Is this an official Anthropic-sponsored training?",
        answer:
          "No. We're an independent agency, and our workshops aren't affiliated with, sponsored by, or endorsed by Anthropic in any way. We teach Claude because we use it every day in our own work and it's the best tool we've found... not because anyone asked us to. Claude and Anthropic are trademarks of Anthropic, PBC.",
      },
    ],
    // Logo bar renders these ~32px tall — use the -480 tier (or the smallest
    // existing variant), not the full-size masters (tmobile.webp is 3561px wide).
    brandLogos: [
      { imageUrl: "/images/client-logos/espn.webp", altText: "ESPN", linkUrl: null },
      { imageUrl: "/images/client-logos/jetblue.webp", altText: "JetBlue", linkUrl: null },
      { imageUrl: "/images/client-logos/chase.webp", altText: "Chase", linkUrl: null },
      { imageUrl: "/images/client-logos/tmobile-480.webp", altText: "T-Mobile", linkUrl: null },
      { imageUrl: "/images/client-logos/pbs.svg", altText: "PBS", linkUrl: null },
      { imageUrl: "/images/client-logos/forbes-2.webp", altText: "Forbes", linkUrl: null },
      { imageUrl: "/images/client-logos/nasdaq-480.webp", altText: "Nasdaq", linkUrl: null },
      { imageUrl: "/images/client-logos/amc-networks-480.webp", altText: "AMC Networks", linkUrl: null },
      { imageUrl: "/images/client-logos/asurion-800.webp", altText: "Asurion", linkUrl: null },
      { imageUrl: "/images/client-logos/hanes-480.webp", altText: "Hanes", linkUrl: null },
      { imageUrl: "/images/client-logos/salesforce.svg", altText: "Salesforce", linkUrl: null },
      { imageUrl: "/images/client-logos/paypal.webp", altText: "PayPal", linkUrl: null },
    ],
    showThingsYoullBuild: false,
    stickyBarTitle: "Master Claude Cowork",
    logoBarEyebrow: "The Claude workshop chosen by some really smart brands.",
    learnHeadlineSuffix: "the Claude Cowork Workshop",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CLAUDE CODE FOR BEGINNERS WORKSHOP
  // ═══════════════════════════════════════════════════════════════════════════
  "claude-code-for-beginners-workshop": {
    // Half day since 2026-10-26 (was 10:00 AM to 5:00 PM with lunch).
    agendaItems: [
      {
        timeStart: "10:00 AM",
        timeEnd: "10:20 AM",
        title: "Welcome & System Setup",
        description:
          "Instructor intro, session overview, live demo of what's possible. Sets tone: no jargon, no gatekeeping.",
        sessionType: "lecture",
      },
      {
        timeStart: "10:20 AM",
        timeEnd: "11:00 AM",
        title: "Setup & First Run",
        description: "Getting Claude Code desktop app installed and running. Every attendee follows along.",
        sessionType: "hands_on",
      },
      {
        timeStart: "11:00 AM",
        timeEnd: "11:45 AM",
        title: "Core Skills Workshop",
        description: "How to give tasks, monitor progress, evaluate output. Live exercises with real use cases.",
        sessionType: "hands_on",
      },
      {
        timeStart: "11:45 AM",
        timeEnd: "12:00 PM",
        title: "Break",
        description: null,
        sessionType: "break",
      },
      {
        timeStart: "12:00 PM",
        timeEnd: "12:50 PM",
        title: "Build Session",
        description: "Attendees build their project: app, landing page, or workflow. Instructor-led, with real-time help.",
        sessionType: "hands_on",
      },
      {
        timeStart: "12:50 PM",
        timeEnd: "1:15 PM",
        title: "Ship + Close",
        description: "Publish your project. Q&A. What to do this week. Resources and community access.",
        sessionType: "qa",
      },
    ],
    learningOutcomes: [
      {
        icon: "Monitor",
        headline: "Understand what Claude Code actually is — and what it does",
        description:
          "We'll install Claude Code together, step by step. You'll have it running on your computer within the first hour — no technical background needed.",
      },
      {
        icon: "ListChecks",
        headline: "Use it in the browser, desktop app, or terminal",
        description:
          "Claude Code works three ways. We cover all of them so you leave knowing which one fits how you actually work.",
      },
      {
        icon: "Rocket",
        headline: "Build real products without writing a single line of code",
        description:
          "Design and ship an app, a landing page, or an internal tool by describing what you want. Claude Code writes the code. You make the decisions.",
      },
      {
        icon: "Globe",
        headline: "Deploy your project to a live website",
        description:
          "Push your finished app or landing page to a real URL through Google Cloud. You will do this yourself, before the session ends.",
      },
    ],
    inclusions: [
      { title: "Workshop Recording" },
      { title: "Lifetime MMI Access" },
      { title: "30-Day SherpaOS Trial" },
    ],
    instructors: [
      {
        name: "Isaac Rudansky",
        title: "Founder, AdVenture Media",
        headshotUrl: "/assets/headshots/headshot_isaac_rudansky_new.webp",
        bio: "Trained 450,000+ marketers and business owners worldwide. Founder of AdVenture Media. Has worked with Forbes, AMC Networks, American Eagle Outfitters, Asurion, Nasdaq, and more to improve performance through AI-native workflows.",
        accomplishments: [
          "450,000+ students trained",
          "Founded AdVenture Media",
          "CEO Modern Marketing Institute",
          "Google Premier Partner",
        ],
        linkedinUrl: "https://linkedin.com/in/isaacrudansky",
        displayOrder: 0,
      },
      {
        name: "Patrick Gilbert",
        title: "Best-Selling Author & CEO, AdVenture Media",
        headshotUrl: "/assets/headshots/headshot_patrick_gilbert.webp",
        bio: 'Patrick is the best-selling author of "Join or Die: Digital Advertising in the Age of Automation." He trains Google in automated bidding and machine learning, and has taught thousands of marketers and business operators how to use AI tools effectively.',
        accomplishments: [
          "Trained 5,000+ professionals on AI tools",
          "Google & Meta certified partner",
        ],
        linkedinUrl: null,
        displayOrder: 1,
      },
      {
        name: "Nechama Teigman",
        title: "Director of AI and Data Analytics, AdVenture Media",
        headshotUrl: "/images/instructors/nechama.webp",
        bio: "Nechama is the Director of AI and Data Analytics at AdVenture Media. She oversees all mixed media modeling and attribution, translating complex data into actionable strategies for clients across industries.",
        accomplishments: [
          "Operations leader at AdVenture Media",
          "AI workflow specialist",
        ],
        linkedinUrl: null,
        displayOrder: 2,
      },
    ],
    faqItems: [
      {
        question: "What exactly is Claude Code?",
        answer:
          "Claude Code is an AI assistant built by Anthropic that can write, edit, and debug code. You interact with it using plain English — describe what you want, and it builds it. It is the most capable coding assistant currently available, and you do not need to know how to code to use it. Think of it as having a senior developer on call who works at the speed of thought.",
      },
      {
        question: "Do I need to be a programmer?",
        answer:
          "No. Not even a little. You don't need to know any programming language, and you won't learn one. Claude Code works by you describing what you want in plain English. The workshop teaches you how to communicate effectively with it — which is a completely different skill from programming.",
      },
      {
        question: "What will I leave with?",
        answer:
          "Three things: (1) Claude Code installed and running on your computer, (2) a real project — an app, a tool, or a website — that you built and published during the workshop, and (3) a repeatable workflow you can use on any future project without our help.",
      },
      {
        question: "How interactive is it?",
        answer: "Very. Live instruction, independent build time, and real-time help throughout.",
      },
      {
        question: "Will it be recorded?",
        answer: "Yes. Registered attendees get access to recordings after the event.",
      },
      {
        question: "What do I need before I arrive?",
        answer:
          "A laptop with internet access and a Claude.ai account (free tier works). We'll send a prep guide 5 days before with setup instructions. There's also an optional setup session 30 minutes before start.",
      },
      {
        question: "What's the refund policy?",
        answer:
          "Yes. If you attend and aren't satisfied, email us within 24 hours of the workshop ending and we'll issue a full refund, no questions asked.",
      },
      {
        question: "How do team seats work?",
        answer:
          "Team seats cover a set number of attendees. One confirmation; you distribute access to your team. Everyone attends together.",
      },
      {
        question: "What if I have to leave early?",
        answer: "You'll have access to recordings and materials regardless.",
      },
      {
        question: "Is this really for beginners?",
        answer:
          "Yes — and we mean it. This workshop was designed from the ground up for people who have never written a line of code, never opened a terminal, and never built software. Every exercise is guided step-by-step with an instructor walking alongside you. If you can type and follow directions, you can do this.",
      },
      {
        question: "What can I build after this workshop?",
        answer:
          "Almost anything you can describe in plain English. Landing pages, internal dashboards, expense trackers, email automations, client report builders, pricing calculators, onboarding portals — the list keeps growing. During the workshop you will build and publish a real project, and you will leave with a repeatable workflow to build more on your own.",
      },
      {
        question: "I am a solo entrepreneur — is this for me?",
        answer:
          "Absolutely. Solo entrepreneurs might get the most out of this workshop. You have ideas that move faster than your capacity to execute them, and you do not have a team to delegate to. Claude Code changes that equation entirely. You will leave this workshop able to build the tools, pages, and automations your business needs — without hiring a developer or learning to code.",
      },
      {
        question: "What is the difference between Claude and Codex?",
        answer:
          "Claude is made by Anthropic. Codex is made by OpenAI. Both are AI assistants, but Claude Code is specifically designed to be a collaborative coding partner that you interact with in plain English. In independent benchmarks, Claude consistently outperforms Codex on complex software engineering tasks. More importantly for this workshop: Claude Code is the tool we have seen deliver the best results for non-technical users building real projects.",
      },
      {
        question: "Is Claude Code the same thing as ChatGPT?",
        answer:
          "No. ChatGPT is a general-purpose chatbot made by OpenAI. Claude Code is a specialized coding assistant made by Anthropic that can write, edit, debug, and deploy entire software projects. While ChatGPT can answer questions and generate text, Claude Code can build you a working app, push it live to the internet, and iterate on it with you until it is exactly what you need. They are fundamentally different tools.",
      },
      {
        question: "Is this an official Anthropic-sponsored training?",
        answer:
          "No. We're an independent agency, and our workshops aren't affiliated with, sponsored by, or endorsed by Anthropic in any way. We teach Claude because we use it every day in our own work and it's the best tool we've found... not because anyone asked us to. Claude and Anthropic are trademarks of Anthropic, PBC.",
      },
    ],
    brandLogos: [
      { imageUrl: "/images/client-logos/espn.webp", altText: "ESPN", linkUrl: null },
      { imageUrl: "/images/client-logos/jetblue.webp", altText: "JetBlue", linkUrl: null },
      { imageUrl: "/images/client-logos/chase.webp", altText: "Chase", linkUrl: null },
      { imageUrl: "/images/client-logos/tmobile-480.webp", altText: "T-Mobile", linkUrl: null },
      { imageUrl: "/images/client-logos/pbs.svg", altText: "PBS", linkUrl: null },
      { imageUrl: "/images/client-logos/forbes-2.webp", altText: "Forbes", linkUrl: null },
      { imageUrl: "/images/client-logos/nasdaq-480.webp", altText: "Nasdaq", linkUrl: null },
      { imageUrl: "/images/client-logos/amc-networks-480.webp", altText: "AMC Networks", linkUrl: null },
      { imageUrl: "/images/client-logos/asurion-800.webp", altText: "Asurion", linkUrl: null },
      { imageUrl: "/images/client-logos/hanes-480.webp", altText: "Hanes", linkUrl: null },
      { imageUrl: "/images/client-logos/salesforce.svg", altText: "Salesforce", linkUrl: null },
      { imageUrl: "/images/client-logos/paypal.webp", altText: "PayPal", linkUrl: null },
    ],
    showThingsYoullBuild: false, // temporarily hidden — awaiting 16:10 sample images
    stickyBarTitle: "Master Claude Code",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CLAUDE FOR BEGINNERS WORKSHOP (new 2026-10-26, Isaac teaches)
  // Positioning (Esther): the Claude training we run for companies, now open to
  // the public. Leads with Claude itself, not Cowork or Code.
  // Agenda and outcomes are a DRAFT built from Isaac's Cowork for Beginners
  // curriculum; Isaac signs off before the date is published.
  // Credentials: only the sourced figures in shared/instructors.ts.
  // Logos: brands confirmed as trained (Esther, 2026-10-26) or present in
  // workshop orders. Do not add agency clients here.
  // ═══════════════════════════════════════════════════════════════════════════
  "claude-for-beginners-workshop": {
    agendaItems: [
      {
        timeStart: "10:00 AM",
        timeEnd: "10:20 AM",
        title: "Welcome + Setup",
        description:
          "Your Claude account, the web and desktop apps, which plan you actually need, and how usage limits work. Everyone is set up before we move on.",
        sessionType: null,
      },
      {
        timeStart: "10:20 AM",
        timeEnd: "10:55 AM",
        title: "Prompting That Works the First Time",
        description:
          "The habits that separate a generic answer from a useful one. You rewrite one of your own real tasks live.",
        sessionType: "hands_on",
      },
      {
        timeStart: "10:55 AM",
        timeEnd: "11:30 AM",
        title: "Projects, Instructions, and Your Files",
        description:
          "Set Claude up so it already knows your work, your voice, and your documents before you type a word.",
        sessionType: "hands_on",
      },
      {
        timeStart: "11:30 AM",
        timeEnd: "11:40 AM",
        title: "Break",
        description: null,
        sessionType: "break",
      },
      {
        timeStart: "11:40 AM",
        timeEnd: "12:15 PM",
        title: "Connect Claude to Your Tools",
        description:
          "Gmail, Google Drive, and your calendar, the privacy settings that control what Claude sees, plus research with sources and analysis of your own files.",
        sessionType: "hands_on",
      },
      {
        timeStart: "12:15 PM",
        timeEnd: "12:45 PM",
        title: "Skills and Finished Work",
        description:
          "Save a repeat task as a skill, put it on a schedule, then make a deck, a document, and a chart you'd actually send.",
        sessionType: "hands_on",
      },
      {
        timeStart: "12:45 PM",
        timeEnd: "1:00 PM",
        title: "Q&A + Your First 30 Days",
        description:
          "Open questions, where Cowork and Claude Code fit next, and a plan for what to use Claude on this week.",
        sessionType: "qa",
      },
    ],
    learningOutcomes: [],
    inclusions: [],
    instructors: [
      {
        name: "Isaac Rudansky",
        title: "Founder, AdVenture Media",
        headshotUrl: "/assets/headshots/headshot_isaac_rudansky_new.webp",
        bio: "Isaac built one of Udemy's best-selling Google Ads courses: more than 237,000 students and a 4.7 rating from over 55,000 reviews. Today he leads the Claude training AdVenture Media runs for teams at Chase, ESPN, and T-Mobile. Direct, practical, and allergic to jargon.",
        accomplishments: [
          "237,000+ students on Udemy",
          "4.7 rating from 55,000+ reviews",
          "Founder, AdVenture Media",
        ],
        linkedinUrl: null,
        displayOrder: 0,
      },
    ],
    faqItems: [
      {
        question: "Why isn't this free like the other Claude webinars?",
        answer:
          "Free webinars are paid for by the pitch at the end. This workshop is the product, so there's no pitch. You get three live hours with Isaac, hands-on the whole time, plus the recording, two weeks of email support, and a certificate. If it isn't worth it, you get your money back.",
      },
      {
        question: "Is this really for beginners?",
        answer:
          "Yes. If you've never opened Claude, you'll be set up in the first 20 minutes. If you've used it a little and it felt underwhelming, this is where that changes. No technical skills needed: if you can write an email, you can do this. If you already use Claude every day with projects and connectors, this one is too basic for you.",
      },
      {
        question: "Do I need a paid Claude plan?",
        answer:
          "A free Claude account is enough to follow along. A few things we cover work best on a paid plan like Claude Pro. We explain what each plan includes and how usage limits work, so you can decide after you've seen it.",
      },
      {
        question: "Is my work private when I use Claude?",
        answer:
          "We walk through the privacy and data settings in your own Claude account during the workshop, so you choose what Claude can see and keep. We also cover what we recommend keeping out of any AI tool at work.",
      },
      {
        question: "Will it be recorded? What if I miss it live?",
        answer:
          "Yes. Every registered attendee gets the full recording and transcript within 48 hours, and keeps access for life. Live is better, because you can ask Isaac questions while you build.",
      },
      {
        question: "Do I get a certificate?",
        answer:
          "Yes. Everyone who attends gets a certificate of completion for the Claude for Beginners Workshop, signed by Isaac.",
      },
      {
        question: "What's the refund policy?",
        answer:
          "If you attend and it wasn't worth it, email us within 24 hours of the workshop ending and we'll refund you in full. No questions asked.",
      },
      {
        question: "How do team seats work?",
        answer:
          "Buy three or more seats at the team price and send the access to your team. Everyone attends the same live session. We can invoice if your company needs a PO.",
      },
      {
        question: "Is this an official Anthropic training?",
        answer:
          "No. We're an independent agency, and our workshops aren't affiliated with, sponsored by, or endorsed by Anthropic in any way. We teach Claude because we use it every day in our own work. Claude and Anthropic are trademarks of Anthropic, PBC.",
      },
    ],
    brandLogos: [
      { imageUrl: "/images/client-logos/chase.webp", altText: "Chase", linkUrl: null },
      { imageUrl: "/images/client-logos/espn.webp", altText: "ESPN", linkUrl: null },
      { imageUrl: "/images/client-logos/jetblue.webp", altText: "JetBlue", linkUrl: null },
      { imageUrl: "/images/client-logos/tmobile-480.webp", altText: "T-Mobile", linkUrl: null },
      { imageUrl: "/images/client-logos/salesforce.svg", altText: "Salesforce", linkUrl: null },
      { imageUrl: "/images/client-logos/pbs.svg", altText: "PBS", linkUrl: null },
      { imageUrl: "/images/client-logos/optimizely.svg", altText: "Optimizely", linkUrl: null },
      { imageUrl: "/images/client-logos/forbes-2.webp", altText: "Forbes", linkUrl: null },
      { imageUrl: "/images/client-logos/nasdaq-480.webp", altText: "Nasdaq", linkUrl: null },
    ],
    showThingsYoullBuild: false,
    stickyBarTitle: "Master Claude in 3 Hours",
    logoBarEyebrow: "Teams we've trained in Claude",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // THE AI-FIRST LEADER WORKSHOP (formerly The AI Fundamentals Workshop;
  // series slug migrated ai-fundamentals-workshop → the-ai-first-leader-workshop
  // on 2026-06-12, old URLs 301 via SERIES_SLUG_RENAMES)
  // ═══════════════════════════════════════════════════════════════════════════
  "the-ai-first-leader-workshop": {
    agendaItems: [
      {
        timeStart: "10:00 AM",
        timeEnd: "10:15 AM",
        title: "The Open: The Lightbulb + Your Three Hats",
        description:
          "The lightbulb analogy that reframes the whole problem, then the map for the day: the three hats you wear (individual, team leader, manager) and why AI rewrites the job at every level.",
        sessionType: "lecture",
      },
      {
        timeStart: "10:15 AM",
        timeEnd: "10:40 AM",
        title: "Block 1: The Why · The Manager Hat",
        description:
          "The Double Helix and the 6 Levels of AI Building, illustrated through 20+ real systems from inside our agency. Including the beautiful AI tool that turned out to be a confident liar. Plus the software shift: why you use 10% of the tools you rent, and how to take back the margin.",
        sessionType: "lecture",
      },
      {
        timeStart: "10:40 AM",
        timeEnd: "11:05 AM",
        title: "Block 2: Become a Builder · The Individual Hat",
        description:
          "The 4x2 Model of Work, why solo is no longer a valid mode, and the Builder/Operator split. Then the bespoke build: the second brain and hub-and-spoke agent system we built when no product fit the workflow.",
        sessionType: "lecture",
      },
      {
        timeStart: "11:05 AM",
        timeEnd: "11:25 AM",
        title: "Hands-On: The Live AI Audit",
        description:
          "Open your AI of choice and run your real task list through it. You walk out with a categorized audit and three real automation candidates for your business. Bring your laptop.",
        sessionType: "hands_on",
      },
      {
        timeStart: "11:25 AM",
        timeEnd: "11:35 AM",
        title: "Break",
        description: "Stretch, refill the coffee.",
        sessionType: "break",
      },
      {
        timeStart: "11:35 AM",
        timeEnd: "11:55 AM",
        title: "Block 3: Getting Your Team On Board · The Team-Leader Hat",
        description:
          "How to handle the 'Am I training my replacement?' fear, build safe spaces to experiment, and run the hackathon playbook that flipped a team from 'Can we use AI for this?' to 'We could just build this.'",
        sessionType: "lecture",
      },
      {
        timeStart: "11:55 AM",
        timeEnd: "12:20 PM",
        title: "Block 4: Proof: How We Actually Work",
        description:
          "Live demos of the real systems we use every day, not staged walkthroughs: the command center, the executive AI chief of staff behind it, and Roger, our open-source research agent you can take home.",
        sessionType: "demo",
      },
      {
        timeStart: "12:20 PM",
        timeEnd: "12:40 PM",
        title: "Hands-On: Design Your Agent System",
        description:
          "Sketch your own setup with pen and paper: your second brain's domains, your primary agent's daily job, your first sub-agent. You leave with the design and the build guide.",
        sessionType: "hands_on",
      },
      {
        timeStart: "12:40 PM",
        timeEnd: "1:00 PM",
        title: "The Close: The Roadmap + Q&A",
        description:
          "A concrete 90-day adoption plan, the build guide for your own AI chief of staff, then open Q&A connecting your specific blockers to the frameworks.",
        sessionType: "qa",
      },
    ],
    learningOutcomes: [
      {
        icon: null,
        headline: "Spot the lightbulb problem in your own company",
        description:
          "The Lightbulb Framework shows you where you're layering AI onto the old model versus genuinely rebuilding around it.",
      },
      {
        icon: null,
        headline: "Build both strands of the Double Helix",
        description: "Internal efficiency and external value. Most companies stall because they only ever work one strand.",
      },
      {
        icon: null,
        headline: "Run the 4x2 Model on every task you own",
        description: "Four modes of work, two ways to execute. Solo is no longer one of the options.",
      },
      {
        icon: null,
        headline: "Map your Builders and your Operators",
        description: "Stop expecting everyone to code. Find who should build, who should operate, and fund both.",
      },
      {
        icon: null,
        headline: "Audit your own work, live, in the room",
        description: "AI reads your actual task list and hands you three automation candidates, ranked by effort.",
      },
      {
        icon: null,
        headline: "Answer the 'Am I training my replacement?' question",
        description:
          "Half your team is quietly asking it. Leave with a playbook for the fear and the safety adoption requires.",
      },
      {
        icon: null,
        headline: "Run the hackathon playbook with your team",
        description: "What worked, what flopped, and the one mistake that quietly created shame and stalled adoption.",
      },
      {
        icon: null,
        headline: "Leave with a 90-day roadmap and an AI chief of staff build path",
        description:
          "Week-by-week milestones, plus the full architecture: second brain, primary agent, hub-and-spoke sub-agents.",
      },
    ],
    inclusions: [
      { title: "Full Session Recording" },
      { title: "Slides and All Frameworks" },
      { title: "90-Day Adoption Roadmap Template" },
      { title: "AI Chief of Staff Build Guide" },
      { title: "The Hackathon Playbook" },
      { title: "Roger on GitHub" },
    ],
    audience: {
      forItems: [
        "You're a CEO or founder who knows AI matters, but you haven't figured out how to rebuild around it.",
        "You're a VP or director tasked with 'making AI happen' and you need frameworks, not another tool.",
        "Your team is split between a few enthusiasts and a quiet, nervous majority.",
        "You've realized that buying AI software isn't the same as becoming AI-first.",
      ],
      notForItems: [
        "Individual practitioners looking for a step-by-step tool tutorial.",
        "Anyone who wants to learn one tool and ship something by end of day. That's the Claude Cowork Workshop.",
      ],
      closingNote: "This workshop is strategic. It's about leading the change, not just doing the work.",
    },
    instructors: [
      {
        name: "Patrick Gilbert",
        title: "CEO, AdVenture Media. Author, Never Always, Never Never and Join or Die.",
        headshotUrl: "/assets/headshots/headshot_patrick_gilbert.webp",
        bio: "CEO of AdVenture Media, author of Never Always, Never Never and Join or Die, and a Google trainer on automated bidding and machine learning. Patrick has spent the last three years rebuilding a working marketing agency around AI: tools, workflows, autonomous agents, and full platforms managing millions in ad spend with real clients and real revenue on the line. The frameworks in this workshop come from real tools, real failures, and real results... not theory.",
        accomplishments: null,
        linkedinUrl: "https://www.linkedin.com/in/patrickjamesgilbert/",
        displayOrder: 0,
      },
      {
        name: "Isaac Rudansky",
        title: "Founder, AdVenture Media. Co-lecturer.",
        headshotUrl: "/assets/headshots/headshot_isaac_rudansky_new.webp",
        bio: "Founder of AdVenture Media. Trained 450,000+ marketers and business owners worldwide. Has worked with Forbes, AMC Networks, American Eagle Outfitters, Asurion, Nasdaq, and more, applying AI-native workflows to performance marketing at scale. Co-lecturing alongside Patrick on the demos and the live audits.",
        accomplishments: null,
        linkedinUrl: "https://www.linkedin.com/in/isaacrudansky",
        displayOrder: 1,
      },
    ],
    faqItems: [
      {
        question: "How is this different from the Claude Cowork Workshop?",
        answer:
          "The Cowork Workshop is for individual practitioners who want to learn the tool and ship something by end of day. The AI-First Leader is strategic: it's for the people who have to lead the change. Frameworks, live proof, two working sessions, and a 90-day plan. If you're not sure which one fits, email clara@adventureppc.com and we'll point you at the right one.",
      },
      {
        question: "Who is this for?",
        answer:
          "CEOs and founders who know AI matters but haven't figured out the rebuild. VPs and directors tasked with 'making AI happen' who need frameworks, not another tool. And leaders whose team splits between a few enthusiasts and a quiet, nervous majority.",
      },
      {
        question: "I'm not technical. Is this still useful?",
        answer:
          "Yes. The frameworks are the point, not the tools. The live demos show you what working systems look like, but you don't need to build them yourself. You need to recognize when your team should... and know who your Builders are.",
      },
      {
        question: "Will I get a recording?",
        answer:
          "Yes. The full session recording is included with every ticket, along with the slides and every framework and template.",
      },
      {
        question: "What's the refund policy?",
        answer: "Yes. If you attend and aren't satisfied, email us within 24 hours of the workshop ending and we'll issue a full refund, no questions asked.",
      },
      {
        question: "Can my team attend together?",
        answer:
          "Yes. Team pricing kicks in at 3 seats ($449 per seat). For an extra $300 on the Individual ticket, the VIP+ upgrade adds a 45-minute private strategy call with Patrick after the workshop, useful if you want to sanity-check your specific 90-day plan.",
      },
      {
        question: "Do I need to read the book first?",
        answer:
          "No. The workshop draws on Chapters 30-33 of Never Always, Never Never, but goes further than the book because we show the proof: live working systems, not just frameworks. If you already have the book, skim Chapter 30 the day before.",
      },
      {
        question: "Is this an official Anthropic-sponsored training?",
        answer:
          "No. We're an independent agency, and our workshops aren't affiliated with, sponsored by, or endorsed by Anthropic in any way. We teach Claude because we use it every day in our own work and it's the best tool we've found... not because anyone asked us to. Claude and Anthropic are trademarks of Anthropic, PBC.",
      },
    ],
    brandLogos: [
      { imageUrl: "/images/client-logos/forbes-2.webp", altText: "Forbes", linkUrl: null },
      { imageUrl: "/images/client-logos/nasdaq.webp", altText: "Nasdaq", linkUrl: null },
      { imageUrl: "/images/client-logos/amc-networks.webp", altText: "AMC Networks", linkUrl: null },
      {
        imageUrl: "/images/client-logos/american-eagle-outfitters.webp",
        altText: "American Eagle Outfitters",
        linkUrl: null,
      },
      { imageUrl: "/images/client-logos/asurion.webp", altText: "Asurion", linkUrl: null },
      { imageUrl: "/images/client-logos/michael-aram.webp", altText: "Michael Aram", linkUrl: null },
    ],
    showThingsYoullBuild: false,
    stickyBarTitle: "The AI-First Leader",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CLAUDE FOR DESIGN WORKSHOP
  // ═══════════════════════════════════════════════════════════════════════════
  "claude-for-design-workshop": {
    agendaItems: [
      /* Esther's agenda, verbatim: her titles, her descriptions, her times.
         10:00 AM to 1:15 PM, continuous, no gaps. */
      {
        timeStart: "10:00 AM",
        timeEnd: "10:20 AM",
        title: "Design Vocabulary",
        description:
          "Learn the language behind strong design, from hierarchy and composition to typography, spacing, and balance, so you can recognize what works and clearly describe what needs to change.",
        sessionType: "lecture",
      },
      {
        timeStart: "10:20 AM",
        timeEnd: "10:40 AM",
        title: "References & Inspiration",
        description:
          "Learn how to collect and use references with intention, then translate what you like about them into a clear visual direction for your own work.",
        sessionType: "hands_on",
      },
      {
        timeStart: "10:40 AM",
        timeEnd: "11:05 AM",
        title: "Build Your Design System",
        description:
          "Define the type, color, spacing, components, and visual rules that shape your brand, creating a system that keeps every design consistent and unmistakably yours.",
        sessionType: "hands_on",
      },
      {
        timeStart: "11:05 AM",
        timeEnd: "11:35 AM",
        title: "Build Your Brief + Skill",
        description:
          "Build a reusable Skill that captures your brand, audience, goals, design standards, and creative direction, giving every new project a stronger starting point.",
        sessionType: "hands_on",
      },
      {
        timeStart: "11:35 AM",
        timeEnd: "12:00 PM",
        title: "Prompting & Iteration",
        description:
          "Learn how to turn an idea into clear creative direction, write better prompts, respond to what you see, and refine the work until it feels considered rather than generated.",
        sessionType: "lecture",
      },
      {
        timeStart: "12:00 PM",
        timeEnd: "12:10 PM",
        title: "Break",
        description: null,
        sessionType: "break",
      },
      {
        timeStart: "12:10 PM",
        timeEnd: "12:40 PM",
        title: "Images & Motion",
        description:
          "Bring your designs to life with imagery, motion, animation, and interaction, and learn where these elements add meaning rather than simply adding more.",
        sessionType: "hands_on",
      },
      {
        timeStart: "12:40 PM",
        timeEnd: "1:00 PM",
        title: "Build & Refine",
        description:
          "Put the full process together. Build a real design, review it with a critical eye, make deliberate adjustments, and take it from an early draft to polished work.",
        sessionType: "hands_on",
      },
      {
        timeStart: "1:00 PM",
        timeEnd: "1:15 PM",
        title: "Publishing",
        description:
          "Learn how to take the finished design beyond the workspace, whether you're sharing a prototype, publishing it live, or moving it into the next stage of production.",
        sessionType: "hands_on",
      },
    ],
    learningOutcomes: [
      {
        icon: null,
        headline: "Design a real landing page",
        description:
          "Brief Claude in plain English and watch a polished page take shape — hero, sections, copy, layout — in minutes, not weeks.",
      },
      {
        icon: null,
        headline: "Pick a brand direction",
        description:
          "Generate type pairings, color palettes, and visual moods. Compare side-by-side and commit to a direction with confidence.",
      },
      {
        icon: null,
        headline: "Write copy that converts",
        description:
          "Turn a fuzzy idea into a sharp headline, sub-head, and CTA. Iterate fast with prompts that actually move the needle.",
      },
      {
        icon: null,
        headline: "Make it mobile responsive",
        description:
          "Get phone, tablet, and desktop layouts that actually look good — without ever opening a CSS file.",
      },
      {
        icon: null,
        headline: "Iterate with tweaks",
        description:
          "Toggle palettes, swap fonts, try three hero variations — all without losing your work or starting over.",
      },
      {
        icon: null,
        headline: "Ship to a live URL",
        description:
          "Push your finished site to a real URL you can share with a client, your team, or your audience — by lunch.",
      },
      {
        icon: null,
        headline: "Build a personal design system",
        description:
          "Walk away with a reusable kit — colors, type, components — Claude can pick up next time you start a new page.",
      },
      {
        icon: null,
        headline: "Know what \"good\" looks like",
        description:
          "Train your eye with critique sessions on real designs. Learn the principles top studios use, in plain language.",
      },
    ],
    inclusions: [
      { title: "Hands-On Training & Expert Design Guidance" },
      { title: "Full Recording Access For Life" },
      { title: "2 Weeks Personal Email Support" },
      { title: "Ready-To-Remix Landing Page Templates" },
      { title: "Prompt Library" },
    ],
    instructors: [
      {
        name: "Isaac Rudansky",
        title: "Founder, AdVenture Media · Co-Host",
        headshotUrl: "/assets/headshots/headshot_isaac_rudansky_new.webp",
        bio: "Founder of AdVenture Media and instructor to more than 450,000 marketers and operators. Isaac has spent nearly two decades at the intersection of marketing, technology, and creative work. He'll co-host the workshop, bringing the broader perspective of how AI is changing the way modern teams create, work, and ship.",
        accomplishments: null,
        linkedinUrl: "https://www.linkedin.com/in/isaacrudansky",
        displayOrder: 1,
      },
      {
        name: "Esther Nadoff",
        title: "Director of Education · Lead Instructor",
        headshotUrl: "/assets/headshots/headshot_esti_nadoff.webp",
        bio: "Designer, educator, and lead designer at the Modern Marketing Institute. Esther has spent years designing brands, campaigns, and digital experiences, and developing a practical workflow for using Claude without sacrificing taste, consistency, or craft. She'll lead the workshop from inspiration and design systems through finished, publishable work.",
        accomplishments: null,
        linkedinUrl: null,
        displayOrder: 0,
      },
    ],
    faqItems: [
      {
        question: "Do I need to know how to code?",
        answer:
          "No. The whole point of the workshop is that you'll never touch a line of code. Claude does the writing, the design, the layout, and the deploy. You bring the taste.",
      },
      {
        question: "What level of design experience do I need?",
        answer:
          "Any level, and that's the point. The room spans total beginners who have never opened a design tool, marketers and founders who design out of necessity, and working designers who just want to move faster. Beginners leave with a finished, shipped page and a process they can repeat. Experienced designers leave with a workflow that offloads the grunt work so they create more, not less. Whatever level you walk in at, you walk out with more than you came with.",
      },
      {
        question: "Will I actually have a shipped page by the end?",
        answer: "Yes. By 1:15 PM ET, your page is on a live URL. We don't end early.",
      },
      {
        question: "Can I attend if I'm not in the US?",
        answer: "Yes — it's Google Meet. You'll get the recording the next day if your timezone is brutal.",
      },
      {
        question: "What's the refund policy?",
        answer:
          "Yes. If you attend and aren't satisfied, email us within 24 hours of the workshop ending and we'll issue a full refund, no questions asked.",
      },
      {
        question: "Is this an official Anthropic-sponsored training?",
        answer:
          "No. We're an independent agency, and our workshops aren't affiliated with, sponsored by, or endorsed by Anthropic in any way. We teach Claude because we use it every day in our own work and it's the best tool we've found... not because anyone asked us to. Claude and Anthropic are trademarks of Anthropic, PBC.",
      },
    ],
    brandLogos: [
      { imageUrl: "/images/client-logos/espn.webp", altText: "ESPN", linkUrl: null },
      { imageUrl: "/images/client-logos/jetblue.webp", altText: "JetBlue", linkUrl: null },
      { imageUrl: "/images/client-logos/chase.webp", altText: "Chase", linkUrl: null },
      { imageUrl: "/images/client-logos/tmobile-480.webp", altText: "T-Mobile", linkUrl: null },
      { imageUrl: "/images/client-logos/pbs.svg", altText: "PBS", linkUrl: null },
      { imageUrl: "/images/client-logos/forbes-2.webp", altText: "Forbes", linkUrl: null },
      { imageUrl: "/images/client-logos/nasdaq-480.webp", altText: "Nasdaq", linkUrl: null },
      { imageUrl: "/images/client-logos/amc-networks-480.webp", altText: "AMC Networks", linkUrl: null },
      { imageUrl: "/images/client-logos/asurion-800.webp", altText: "Asurion", linkUrl: null },
      { imageUrl: "/images/client-logos/hanes-480.webp", altText: "Hanes", linkUrl: null },
      { imageUrl: "/images/client-logos/salesforce.svg", altText: "Salesforce", linkUrl: null },
      { imageUrl: "/images/client-logos/paypal.webp", altText: "PayPal", linkUrl: null },
    ],
    showThingsYoullBuild: false,
    stickyBarTitle: "Claude for Design",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CLAUDE FOR SALES TEAMS WORKSHOP
  // ═══════════════════════════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════════════════════════
  // CLAUDE FOR SALES
  // Rebuilt against Ronnie Cardno's curriculum brief (Aug 2026):
  // claude.ai/code/artifact/af9bbfa3-cd51-4506-bbdb-b1e8e753f7a9
  //
  // What changed from the previous entry: the workshop is now a Foundation
  // (the profile) plus THREE engines (Research, Outreach, Recap) plus the
  // method to build more, over 3.5 hours rather than 3. The old "live co-pilot
  // on every call" and "CRM integration" beats are gone; they aren't in the
  // curriculum. Pricing is Claude Design's, which is what she asked for twice:
  // $799 solo / $599 at three seats / +$999 VIP (Esther, 2026-09-01 "whatever
  // we've been running for claude design", and again 2026-09-09 when the VIP
  // came up on its own).
  //
  // THE VIP WAS WRONG UNTIL 2026-09-09 and the reason is worth keeping: her
  // instruction named Claude Design, Design's VIP is $999, and it was written
  // down here as $300. The seat prices were copied correctly and the add-on was
  // not. Match the named series, do not re-derive a number from the shape of
  // the offer.
  //
  // TWO HARD RULES ON THIS PAGE:
  //  1. Never the word "templates." Every output is written fresh, live, in
  //     the seller's own voice. "Template" reads as mail-merge.
  //  2. No invented research. The 72% figure below is the one external stat
  //     carried over from the original page copy; anything new needs a source
  //     before it ships. The brief asks for Stanford/MIT adoption research in
  //     the skeptic FAQ, which is Ronnie's to supply.
  // ═══════════════════════════════════════════════════════════════════════════
  "claude-for-sales-teams-workshop": {
    /* AGENDA COPY RULES, since this was rewritten once for legibility (Esther,
       2026-08-28: "fix the agenda to be easier to understand and read"):

       1. TITLES SAY THE THING YOU BUILD. "Engine 1: research" made the reader
          hold a numbering scheme in their head to know what a row was about.
       2. ONE SENTENCE OF WHAT HAPPENS, THEN WHAT YOU LEAVE WITH. The old rows
          ran to three clauses and ended on abstractions — "That gap is the whole
          workshop", "Everything after this reads from it" — which read as
          commentary on the agenda rather than as the agenda.
       3. NUMBERS MUST MATCH THE REST OF THE PAGE. Two did not. The heading said
          "four engines" while every other section says a profile plus three, and
          this row said "Bring one real prospect" while the hero promises three.
          A reader who notices either one stops trusting the schedule. */
      /* ESTHER'S AGENDA, WRITTEN BY HER AND USED AS WRITTEN (2026-09-08). Eight
         blocks with her own times, headings and bodies. Four passes of mine came
         before it, all of them worse, so the only notes here are about what the
         renderer forced:

           · HER PARAGRAPH BREAKS ARE JOINED. Three blocks arrived as two
             paragraphs ("Bring a company you're actually trying to sell to."
             then the body). AgendaTimeline renders `description` as a single
             <p>, so a blank line would collapse to a space regardless. Joined
             deliberately rather than left to collapse.
           · BREAK HAS NO DESCRIPTION, which is hers. The renderer guards on
             `item.description &&`, so an empty string renders nothing rather
             than an empty paragraph.
           · TITLE CASE IS HERS. Every other agenda on the site is sentence
             case; she wrote these as headings and they are her headings.

         210 minutes, 10:00 to 1:30 ET: 20 + 25 + 40 + 15 + 35 + 35 + 30 + 10. */
      agendaItems: [
        {
          timeStart: "10:00 AM",
          timeEnd: "10:20 AM",
          title: "See What Claude Can Do",
          description:
            "We'll start with a live demo using a real company. You'll see how Claude can research a prospect, organize what matters, and turn it into something useful for sales.",
          sessionType: "lecture",
        },
        {
          timeStart: "10:20 AM",
          timeEnd: "10:45 AM",
          title: "Set Claude Up for How You Sell",
          description:
            "Claude will interview you about what you sell, who you sell to, how you qualify leads, what makes you different, and how you write. You'll leave this section with a saved sales profile and writing voice that we'll use throughout the rest of the workshop.",
          sessionType: "hands_on",
        },
        {
          timeStart: "10:45 AM",
          timeEnd: "11:25 AM",
          title: "Research a Real Prospect",
          description:
            "Bring a company you're actually trying to sell to. You'll build a reusable research tool that looks into the company, key people, recent news, competitors, potential needs, and other useful sales signals. You'll leave with a finished prospect brief you can actually use.",
          sessionType: "hands_on",
        },
        {
          timeStart: "11:25 AM",
          timeEnd: "11:40 AM",
          title: "Break",
          description: "",
          sessionType: "break",
        },
        {
          timeStart: "11:40 AM",
          timeEnd: "12:15 PM",
          title: "Build Your Outreach Tool",
          description:
            "Now we'll use the research to write better outreach. You'll build a reusable skill that can write first emails, follow-ups, and re-engagement messages in your own voice, based on the prospect you're talking to.",
          sessionType: "hands_on",
        },
        {
          timeStart: "12:15 PM",
          timeEnd: "12:50 PM",
          title: "Turn Calls Into Follow-Up",
          description:
            "Give Claude your call notes or transcript. You'll show it how to pull out the important points, objections, next steps, and deal status, then turn that into a follow-up you can actually send.",
          sessionType: "hands_on",
        },
        {
          timeStart: "12:50 PM",
          timeEnd: "1:20 PM",
          title: "Put It All Together",
          description:
            "This is where the pieces start working together. Research leads into outreach. Calls lead into follow-up. Deal information stays organized. You'll see how to turn what you built into a proactive AI sales agent that can run alongside your real deals and help with the work that happens before and after the conversation.",
          sessionType: "hands_on",
        },
        {
          timeStart: "1:20 PM",
          timeEnd: "1:30 PM",
          title: "Take It With You",
          description:
            "We'll make sure everything is saved and ready to use. You'll leave with your sales profile, research tool, outreach tool, recap tool, deal tracker, and the know-how to keep building new tools in Claude on your own.",
          sessionType: "lecture",
        },
      ],
    learningOutcomes: [
      {
        icon: null,
        headline: "Teach the AI who you are, once.",
        description:
          "A guided interview captures what you sell, your ideal buyer, how you qualify, and your writing voice. This is what separates output that could be about anyone from output tuned to you.",
      },
      {
        icon: null,
        headline: "Turn any company name into a sourced brief.",
        description:
          "What they do, how big they are, what just changed, the decision makers with their LinkedIn profiles, reputation, competitors, and contacts. It cites where every fact came from.",
      },
      {
        icon: null,
        headline: "Write outreach in your own voice, fresh every time.",
        description:
          "First touches, follow-ups, and re-engagement grounded in the research. Nothing filled in around a blank, and none of the AI smell buyers catch in the first line.",
      },
      {
        icon: null,
        headline: "End every call with the next step already drafted.",
        description:
          "Drop in the call. Out come clean notes, an updated deal status, and the follow-up written and waiting. Nothing slips while you're in the next conversation.",
      },
      {
        icon: null,
        headline: "Build engines that call other engines.",
        description:
          "Your recap engine uses your outreach engine to write the follow-up. Once you've seen skills compose, the ceiling on what you can build disappears.",
      },
      {
        icon: null,
        headline: "Build any tool your pipeline needs, forever.",
        description:
          "The three engines are the demonstration. The repeatable method is the takeaway. When your pipeline needs a fourth thing, you build it that afternoon.",
      },
      {
        icon: null,
        headline: "Come away genuinely fluent in Claude.",
        description:
          "Skills, connectors, artifacts, memory and projects, and how to direct the AI so it gives you what you asked for. Real hands-on fluency, with your own pipeline as the training ground.",
      },
      {
        icon: null,
        headline: "Leave with the system running, not with notes.",
        description:
          "Everything is built during the session on prospects already in your pipeline. It's on your machine and working before you log off.",
      },
    ],
    // The brief's "10+ ready-to-use assets, theirs to keep". Deliberately no
    // "template" anywhere in this list.
    inclusions: [
      { title: "Full Session Recording" },
      { title: "Your Sales Profile, Configured To How You Sell" },
      { title: "Your Writing Voice, Captured" },
      { title: "The Research Engine, Saved As A Skill" },
      { title: "The Outreach Engine, Saved As A Skill" },
      { title: "The Recap Engine, Saved As A Skill" },
      { title: "A Finished Intel Brief On A Real Prospect" },
      { title: "Your Own Deal Tracker" },
      { title: "Day 7, 14, 30 Follow-Ups" },
    ],
    /* ALL THREE BIOS AND BOTH TITLES ARE ESTHER'S, VERBATIM (2026-09-09).
       Five drafts of mine came first and every one was worse, in the same way
       each time: I wrote what the instructor does to their OWN work, and she
       writes what the instructor does for the BUYER. "she helps you improve
       your outreach", "shows you how to use AI around real sales
       conversations". docs/copy-guide.md asks for that in as many words ("Use
       'you' and 'your' to speak directly to the prospect") and I read it twice
       without applying it. Change these only on her say-so.

       Titles stored Title Case with her ampersand. InstructorsBandSection.tsx
       applies textTransform: uppercase, so the page renders the caps she wrote
       while the stored string keeps AdVenture's own capitalisation.

       THE VIP DEAL-COACHING SESSION left Brigid's bio in this pass. It is a
       $999 upgrade and still sold in the pricing FAQ answer below, which names
       the private 45-minute session with her, so the fact survives where a
       buyer decides. Flagged in case she wants it back here too. */
    // Bios cut by half at Isaac's request (2026-09-09 review); Esther's full versions are in git history.
    instructors: [
      {
        name: "Ronnie Cardno",
        title: "AI Architect & Sales Executive, AdVenture Media",
        headshotUrl: "/images/headshots/ronnie-cardno.webp",
        bio: "Ronnie built the AI sales system he runs every day at AdVenture Media. He tested every piece on real deals first.",
        accomplishments: null,
        linkedinUrl: null,
        displayOrder: 0,
      },
      {
        name: "Brigid Healy",
        title: "AI Architect & Sales Executive, AdVenture Media",
        headshotUrl: "/images/headshots/brigid-healy.webp",
        bio: "Brigid runs AdVenture Media's pipeline alongside Ronnie. In the room, she makes your outreach sound like you, not like software.",
        accomplishments: null,
        linkedinUrl: null,
        displayOrder: 1,
      },
      {
        name: "Isaac Rudansky",
        title: "Founder, AdVenture Media",
        headshotUrl: "/assets/headshots/headshot_isaac_rudansky_new.webp",
        bio: "Isaac founded AdVenture Media and has trained over 450,000 marketers. He works his own deals on the system Ronnie and Brigid built.",
        accomplishments: null,
        linkedinUrl: "https://www.linkedin.com/in/isaacrudansky",
        displayOrder: 2,
      },
    ],
    // The skeptic questions come first, on purpose: they're the ones that stop
    // a seller from registering. Practical logistics follow.
    faqItems: [
      {
        question: "Won't AI-written emails sound like AI?",
        answer:
          "They do when the AI has never met your buyer and has no idea what you sound like. That's most of what you've seen. The first thing you build here is the profile, and the second is a writing agent trained on your own voice, working from research it can cite. Then Brigid reads the output back to the room and we rewrite until it stops sounding like software. Nothing auto-sends. You read every email before it goes.",
      },
      {
        question: "I'm not technical. Is this going to lose me?",
        answer:
          "No. There's no coding and no terminal in this workshop, at any point. Everything happens in the Claude desktop app, which is an app like any other. Claude interviews you to build your profile, so you're answering questions rather than staring at a blank prompt. If you can write an email, you can do this.",
      },
      {
        question: "Will I actually still be using this in a month?",
        answer:
          "That's the right question to ask about any training, and it's why we build on your real pipeline instead of a demo account. You leave with the engines already running on three live prospects, so there's nothing to set up on Monday. You also get day 7, 14, and 30 follow-ups from us. And the last block of the morning is a reuse plan, not a summary slide.",
      },
      {
        question: "Are these prompts you're handing out?",
        answer:
          "No. You're building reusable skills, and every output they produce is written fresh, live, from your profile and real research. There's no fill-in-the-blank anywhere in this. The engines are yours: named, saved, and editable, and you'll know how to build the next one.",
      },
      {
        question: "Who is this for?",
        answer:
          "Anyone who carries a number. Inside sales or field, inbound or outbound, one seller or a whole team, in any industry we've come across: software, real estate, insurance, services, manufacturing. The system tunes to what you sell, so the briefs cover what matters in your market rather than somebody else's.",
      },
      {
        question: "What do I need before the workshop?",
        answer:
          "Four things. A paid Claude plan (Pro or higher, since the free tier will stop you partway through the hands-on work), the Claude desktop app on Mac or Windows, a laptop, and three real prospects you're actually working. Bring the messy ones. If the paid plan is a blocker for you or your team, email clara@adventureppc.com before you register and we'll work it out.",
      },
      {
        question: "What about data privacy? I can't put customer data into AI tools.",
        answer:
          "Fair concern, and we cover it in the room. Paid Claude Pro and Team accounts don't use your conversations to train the model. We'll also cover what changes under Claude Enterprise, and the redaction habits Brigid uses on our own pipeline. If your company already runs Claude Enterprise, your existing setup works for everything we teach.",
      },
      {
        question: "How long is it, and is it live?",
        answer:
          "Half a day, about three and a half hours, live on Zoom in a small cohort. Hands-on the entire way through, with real instructors in the room answering real questions. It isn't a recorded course you watch alone.",
      },
      {
        question: "Can my team come together?",
        answer:
          /* PRICES ARE WRITTEN OUT HERE AND MUST MATCH THE DATABASE. This answer is prose,
             so nothing derives it from event_ticket_types — it said $549/$399 (Ronnie's
             brief) while checkout charged $599/$449, and both were wrong once the series
             moved onto Claude Design's $799/$599 on 2026-09-01. If the ticket rows move
             again, this sentence has to move with them or the page quotes one number and
             Stripe charges another. Source of truth: scripts/update-claude-for-sales-pricing.ts */
          "Yes, and it works better that way, because your reps end up with engines that all read from the same view of how your team sells. A single seat is $799. At three seats or more it's $599 per seat. For $999 more on any ticket, the VIP upgrade adds a private 45-minute deal-coaching session with Brigid on one of your real deals after the workshop.",
      },
      {
        question: "Will I get a recording?",
        answer:
          "Yes. The full session recording comes with every ticket, along with everything you build during the morning.",
      },
      {
        question: "What's the refund policy?",
        answer:
          "If you attend and it wasn't worth it, email us within 24 hours of the workshop ending and we'll refund you in full. No questions asked.",
      },
      {
        question: "Is this an official Anthropic-sponsored training?",
        answer:
          "No. We're an independent agency, and our workshops aren't affiliated with, sponsored by, or endorsed by Anthropic in any way. We teach Claude because we use it every day in our own work and it's the best tool we've found... not because anyone asked us to. Claude and Anthropic are trademarks of Anthropic, PBC.",
      },
    ],
    // Logos render 40px tall, so the 320px variants cover 2x screens; the
      // 480/800px files were 2-4x oversized (Lighthouse, 2026-09-09).
      brandLogos: [
      /* THE SAME TWELVE THE COWORK AND CODE PAGES USE (Esther, 2026-08-28:
         "logo grid please use same logos as the cowork page"), copied verbatim
         from the Cowork entry rather than re-picked, so the three pages cannot
         drift apart.

         This replaces a Sales-only set of six. Twelve also fills the mobile
         band, which is `grid-cols-3`: four complete rows instead of the 3x2 that
         prompted the question. T-Mobile and PayPal are both named in the grid's
         `isCompact` list, so their wordmarks render at the shorter height. */

      { imageUrl: "/images/client-logos/espn-320.webp", altText: "ESPN", linkUrl: null },
      { imageUrl: "/images/client-logos/jetblue-320.webp", altText: "JetBlue", linkUrl: null },
      { imageUrl: "/images/client-logos/chase-320.webp", altText: "Chase", linkUrl: null },
      { imageUrl: "/images/client-logos/tmobile-320.webp", altText: "T-Mobile", linkUrl: null },
      { imageUrl: "/images/client-logos/pbs.svg", altText: "PBS", linkUrl: null },
      { imageUrl: "/images/client-logos/forbes-2-320.webp", altText: "Forbes", linkUrl: null },
      { imageUrl: "/images/client-logos/nasdaq-320.webp", altText: "Nasdaq", linkUrl: null },
      { imageUrl: "/images/client-logos/amc-networks-320.webp", altText: "AMC Networks", linkUrl: null },
      { imageUrl: "/images/client-logos/asurion-320.webp", altText: "Asurion", linkUrl: null },
      { imageUrl: "/images/client-logos/hanes-320.webp", altText: "Hanes", linkUrl: null },
      { imageUrl: "/images/client-logos/salesforce.svg", altText: "Salesforce", linkUrl: null },
      { imageUrl: "/images/client-logos/paypal-320.webp", altText: "PayPal", linkUrl: null },
    ],
    showThingsYoullBuild: false,
    stickyBarTitle: "Claude for Sales",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ADVANCED CLAUDE CODE WORKSHOP (Part II)
  // ═══════════════════════════════════════════════════════════════════════════
  "advanced-claude-code-workshop": {
    agendaItems: [
      {
        timeStart: "10:00 AM",
        timeEnd: "10:15 AM",
        title: "The Mental Model",
        description:
          "Why Claude sounds equally confident when it's right and when it's wrong, why it agrees with you under pushback, why it forgets your project between sessions. One framework explains all of it. One habit defends against all of it.",
        sessionType: "lecture",
      },
      {
        timeStart: "10:15 AM",
        timeEnd: "10:40 AM",
        title: "The Story",
        description:
          "A real team shipped a tool that ran 19,944 successful requests before anyone noticed Claude had built ten serious security holes into it. You'll see exactly where they were and what to check on your own builds.",
        sessionType: "lecture",
      },
      {
        timeStart: "10:40 AM",
        timeEnd: "11:25 AM",
        title: "The Three Blind Spots",
        description:
          "The three categories Claude quietly skips on every build: security, correctness, and operations. Plus the five-minute checks that catch each one before your users do.",
        sessionType: "lecture",
      },
      {
        timeStart: "11:25 AM",
        timeEnd: "11:35 AM",
        title: "Break",
        description: "10 minutes.",
        sessionType: "break",
      },
      {
        timeStart: "11:35 AM",
        timeEnd: "12:15 PM",
        title: "Using Claude Deliberately",
        description:
          "The moves senior engineers use when stakes are real. Making Claude argue with itself, surviving multi-day projects, recovering bad sessions, and trusting Claude to run while you sleep.",
        sessionType: "hands_on",
      },
      {
        timeStart: "12:15 PM",
        timeEnd: "1:00 PM",
        title: "Build & Wrap",
        description:
          "You leave with three tools you'll use that very afternoon: a personal Claude rules file, an audit prompt for your own shipped apps, and the one habit that compounds everything else from the day.",
        sessionType: "hands_on",
      },
    ],
    learningOutcomes: [
      {
        icon: null,
        headline: "Predict why Claude fails before it fails",
        description:
          "The five mechanisms behind every confident wrong answer Claude gives, and how to spot each one in the moment.",
      },
      {
        icon: null,
        headline: "Run the second-pass prompt every time",
        description:
          "The single habit that catches 80% of what Claude doesn't volunteer. Thirty seconds, three templates worth memorizing.",
      },
      {
        icon: null,
        headline: "Read a real story from inside our stack",
        description:
          "Ten issues Claude built into one of our own systems and never mentioned, anonymized. The pattern behind each.",
      },
      {
        icon: null,
        headline: "Answer the five security questions before shipping",
        description:
          "Who can call this. What they can do. Whose authority it acts under. What if the secret leaks. How you find out something went wrong.",
      },
      {
        icon: null,
        headline: "Verify with real data, not the chat summary",
        description:
          "The cardinal rule of shipping anything Claude built: use it like a real user, with real data, on the actual deployed thing.",
      },
      {
        icon: null,
        headline: "Use the workflow handles you didn't know you had",
        description:
          "Plan mode, subagents, worktrees, git as a safety net. The handles besides prompting that solve most 'tell Claude better' moments.",
      },
      {
        icon: null,
        headline: "Build the autonomous agent checklist",
        description:
          "Seven things every Claude agent needs before it runs unattended. Kill switch, budget cap, alive signal, audit trail, and the rest.",
      },
      {
        icon: null,
        headline: "Walk out with artifacts, not notes",
        description:
          "Your personalized CLAUDE.md. Your auditor prompt. One shipped slash command, hook, or scheduled agent from the build block.",
      },
    ],
    inclusions: [
      { title: "Full Session Recording" },
      { title: "Your Personalized CLAUDE.md" },
      { title: "The Fresh-Session Auditor Prompt" },
      { title: "The 30-Minute Incident Response Checklist" },
      { title: "One Shipped Artifact" },
      { title: "Day 7, 14, 30 Follow-Ups" },
    ],
    instructors: [
      {
        name: "Nechama Teigman",
        title: "Senior Data Analyst, AdVenture Media. Lead instructor.",
        headshotUrl: "/images/instructors/nechama.webp",
        bio: "Senior data analyst at AdVenture Media. Past the day-to-day of attribution models and dashboards, Nechama spends most of her time building the agency's AI infrastructure: internal MCP servers, autonomous agents, and the systems the rest of the team uses every day. The blind spots in this workshop came from real incidents in those systems. The fixes are what we ship to use ourselves.",
        accomplishments: null,
        linkedinUrl: null,
        displayOrder: 0,
      },
      {
        name: "Isaac Rudansky",
        title: "Founder, AdVenture Media. Co-host.",
        headshotUrl: "/assets/headshots/headshot_isaac_rudansky_new.webp",
        bio: "Founder of AdVenture Media. Has trained 450,000+ marketers and business owners worldwide. Created the Claude Code workshop series and runs Part I. Co-hosts the live audits and the build block, where the lens shifts from understanding the blind spot to shipping the fix that day.",
        accomplishments: null,
        linkedinUrl: "https://www.linkedin.com/in/isaacrudansky",
        displayOrder: 1,
      },
      {
        name: "Patrick Gilbert",
        title: "CEO, AdVenture Media. Co-host. Author, Never Always, Never Never.",
        headshotUrl: "/assets/headshots/headshot_patrick_gilbert.webp",
        bio: "CEO of AdVenture Media and author of Never Always, Never Never. Has spent the last two years building AI systems inside a real marketing agency: tools, workflows, autonomous agents, and full platforms managing millions in ad spend. Co-hosts the workflow-and-agents section, where the conversation moves from individual Claude sessions to systems Claude runs without you watching.",
        accomplishments: null,
        linkedinUrl: "https://www.linkedin.com/in/patrickjamesgilbert/",
        displayOrder: 2,
      },
    ],
    faqItems: [
      {
        question: "How is this different from the Claude Code workshop (Part I)?",
        answer:
          "Part I teaches you to ship something with Claude. Part II teaches you what to do when 'I shipped it' isn't enough. Different audience, different problem. If you've never used Claude Code, Part I is the right starting point. If you've already shipped a few things and noticed the same problems keep coming back, you're ready for Part II.",
      },
      {
        question: "I haven't taken Part I. Can I still come?",
        answer:
          "If you've shipped something with Claude or Claude Code, yes. The workshop assumes you've used the tool and made some real things work. We don't teach prompt basics here. If you haven't built anything with Claude yet, take Part I first. It'll save you both hours.",
      },
      {
        question: "I'm a working developer. Won't this be too basic?",
        answer:
          "No. The audience is a mix of marketers, founders, and working devs. The mental-model section is dense for everyone. The blind-spots section is where developers usually find things they didn't know. The build block is where you ship a slash command, hook, or scheduled agent that runs in your own stack.",
      },
      {
        question: "I don't code professionally. Won't this be too advanced?",
        answer:
          "No. Most of the work is reading what Claude does, asking the right questions, and shipping artifacts that don't require deep coding. Examples: prompts, CLAUDE.md rules, hooks that block dangerous commands. You don't need to write code. You need to know what to make Claude do, and how to check the results.",
      },
      {
        question: "Will I get a recording?",
        answer: "Yes. Full session recording included with every ticket, along with every template, prompt, and checklist from the workshop.",
      },
      {
        question: "Can my team come together?",
        answer:
          "Yes. Team pricing kicks in at three seats ($449 per seat). For an extra $300 on the Individual ticket, the VIP upgrade adds a 45-minute live audit of your shipped Claude app, run by Nechama after the workshop. Useful if you want a second pair of eyes on something you've already deployed.",
      },
      {
        question: "What's the refund policy?",
        answer: "Yes. If you attend and aren't satisfied, email us within 24 hours of the workshop ending and we'll issue a full refund, no questions asked.",
      },
      {
        question: "Is this an official Anthropic-sponsored training?",
        answer:
          "No. We're an independent agency, and our workshops aren't affiliated with, sponsored by, or endorsed by Anthropic in any way. We teach Claude because we use it every day in our own work and it's the best tool we've found... not because anyone asked us to. Claude and Anthropic are trademarks of Anthropic, PBC.",
      },
    ],
    brandLogos: [
      { imageUrl: "/images/client-logos/forbes-2.webp", altText: "Forbes", linkUrl: null },
      { imageUrl: "/images/client-logos/nasdaq.webp", altText: "Nasdaq", linkUrl: null },
      { imageUrl: "/images/client-logos/amc-networks.webp", altText: "AMC Networks", linkUrl: null },
      {
        imageUrl: "/images/client-logos/american-eagle-outfitters.webp",
        altText: "American Eagle Outfitters",
        linkUrl: null,
      },
      { imageUrl: "/images/client-logos/asurion.webp", altText: "Asurion", linkUrl: null },
      { imageUrl: "/images/client-logos/michael-aram.webp", altText: "Michael Aram", linkUrl: null },
    ],
    showThingsYoullBuild: false,
    stickyBarTitle: "Advanced Claude Code",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // AI VIDEO WORKSHOP FOR BEGINNERS — Dave Spector (2026-10-23)
  // Content authority: Dave's brief (relayed by Esther). Four hours, hands-on,
  // everyone leaves with a first draft of their own commercial or short film.
  // Dave especially wants curious business owners in the room: the copy shows
  // how easy AI video looks and then where it gets hard, without talking down.
  //
  // DRAFTS TO CONFIRM BEFORE LAUNCH:
  //  · The agenda below is a proposed four-hour shape built from Dave's topics,
  //    pinned to the house 10 AM ET start. Dave owns the real running order.
  //  · The tools FAQ promises a tool list before the session; Dave to confirm.
  //  · Date, price and the events row are Isaac's (no row exists yet).
  // ═══════════════════════════════════════════════════════════════════════════
  "ai-video-workshop-for-beginners": {
    // No schedule on this page (Dave, 2026-10-27). An empty list hides the
    // agenda band. He runs teaching + exercises, then groups build together
    // in hour three and screen in hour four.
    agendaItems: [],
    learningOutcomes: [],
    inclusions: [
      { title: "Four Hours of Hands-On Training With Dave" },
      { title: "Full Recording Access For Life" },
      { title: "2 Weeks Personal Email Support" },
    ],
    instructors: [
      {
        name: "Dave Spector",
        title: "Creative Director, AdVenture Media · Instructor",
        headshotUrl: "/assets/headshots/headshot_dave_spector.webp",
        bio: "A 2026 Webby Award-nominated director with more than 20 years in filmmaking and education, Dave is the Creative Director behind our video advertising. He’s been integrating generative AI into professional video production since 2022.",
        accomplishments: null,
        linkedinUrl: null,
        displayOrder: 0,
      },
    ],
    faqItems: [
      {
        question: "Do I need any experience with AI or video?",
        answer:
          "No. No filmmaking background required. If you can type a sentence and you’re curious about what AI video can do, you’re ready.",
      },
      {
        question: "What will I actually leave with?",
        answer:
          "A first draft of your own commercial or short film, made from the images and clips you create during the workshop. Or, if you’re not ready to develop your own project, we’ll provide the assets and partners so you can collaborate on one.",
      },
      {
        question: "I run a business. Can I make my own ads after this?",
        answer:
          "You’ll leave with a first draft of your own video and a better understanding of what it takes to produce professional-quality AI content. AI video can look easy at first, but creating believable characters, natural movement, and visual consistency from shot to shot takes skill and practice.\n\nSome participants will continue developing their projects independently. Others may decide to work with a professional creative team. Either way, you’ll be making that decision based on firsthand experience.",
      },
      {
        question: "Which AI tools will we use?",
        answer:
          "You’ll work hands-on with MiniMax H3 Max, which delivers results in seconds rather than minutes. We’ll also use Nano Banana Pro for image generation and ElevenLabs for voiceovers. You’ll have access to most major AI models as you develop your own project.",
      },
      {
        question: "Is it live?",
        answer:
          "Yes! It’s a four-hour, live workshop on Zoom, led by Dave with real-time instruction and feedback. You’ll create images and video clips throughout the session, not just watch demonstrations. The workshop will be conversational and interactive, with plenty of opportunities to ask questions, share ideas, and learn from each other.",
      },
      {
        question: "Will there be a recording?",
        answer:
          "Yes. Every attendee gets lifetime access to the recording, so you can rewatch any step while you keep working on your project.",
      },
      {
        question: "What's the refund policy?",
        answer:
          "If you attend and aren't satisfied, email us within 24 hours of the workshop ending and we'll issue a full refund, no questions asked.",
      },
    ],
    brandLogos: [
      { imageUrl: "/images/client-logos/espn.webp", altText: "ESPN", linkUrl: null },
      { imageUrl: "/images/client-logos/jetblue.webp", altText: "JetBlue", linkUrl: null },
      { imageUrl: "/images/client-logos/chase.webp", altText: "Chase", linkUrl: null },
      { imageUrl: "/images/client-logos/tmobile-480.webp", altText: "T-Mobile", linkUrl: null },
      { imageUrl: "/images/client-logos/pbs.svg", altText: "PBS", linkUrl: null },
      { imageUrl: "/images/client-logos/forbes-2.webp", altText: "Forbes", linkUrl: null },
      { imageUrl: "/images/client-logos/nasdaq-480.webp", altText: "Nasdaq", linkUrl: null },
      { imageUrl: "/images/client-logos/amc-networks-480.webp", altText: "AMC Networks", linkUrl: null },
      { imageUrl: "/images/client-logos/asurion-800.webp", altText: "Asurion", linkUrl: null },
      { imageUrl: "/images/client-logos/hanes-480.webp", altText: "Hanes", linkUrl: null },
      { imageUrl: "/images/client-logos/salesforce.svg", altText: "Salesforce", linkUrl: null },
      { imageUrl: "/images/client-logos/paypal.webp", altText: "PayPal", linkUrl: null },
    ],
    showThingsYoullBuild: false,
    stickyBarTitle: "AI Video Workshop",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // AI SECURITY WORKSHOP — Kate Marshall (TheGr.ai). First session Wed
  // Nov 18 2026, 12-2 PM ET, two hours, Zoom, no break.
  //
  // Built from Patrick and Kate's Nov 2 call. The three modules are Kate's
  // (what can be seen / what can be faked / what you can control); the
  // exercises and the private-model demo are what Patrick asked her to add so
  // people have an aha moment before the session ends and a kit after it.
  //
  // POSITIONING, KATE'S OWN LIMIT: she trains people in plain language and is
  // "inch deep, mile wide". She said she cannot help with incident response,
  // forensics or industrial control systems. Nothing on this page may call her
  // a security practitioner or promise expert Q&A. The notFor list says so.
  //
  // Bio and headshot: Kate's own approved text and photo (email, 2026-10-26).
  // Her bio says "cybersecurity training", not practitioner; keep it that way.
  //
  // DRAFTS TO CONFIRM BEFORE LAUNCH:
  //  · No agenda band on purpose: Kate owns the running order and has not
  //    given times. An empty list hides it.
  //  · Tickets are on the events row, not here: $249 individual, $189 per
  //    seat for 3+ (Patrick, 2026-11-02).
  // ═══════════════════════════════════════════════════════════════════════════
  "ai-security-workshop": {
    // Patrick approved, 2026-11-02. Proof lines are real incidents or
    // documented defaults (see the marketing brief); keep them that way.
    hero: {
      kicker: "The AI Privacy & Security Masterclass",
      headline: "You know what AI can do for you. Here's what it can put at risk.",
      subtitle: "Every AI tool you use brings new privacy, security and fraud risks. In just 2 hours, 18-year cybersecurity veteran Kate Marshall walks you through the practical steps you can take to keep using AI without putting your data, your accounts or your family at risk.",
      proofLines: [
        { label: "Privacy", text: "Many AI plans can train on your chats unless you turn that off." },
        { label: "Security", text: "One hidden instruction in an email made an AI assistant forward dozens of private messages." },
        { label: "Fraud", text: "A few seconds of audio is enough to clone someone's voice." },
      ],
    },
    agendaItems: [],
    learningOutcomes: [
      {
        icon: null,
        headline: "Know what your AI tools can see.",
        description:
          "Which plans let your chats be used to train models, where that setting lives in ChatGPT, Claude and Gemini, and how to turn it off. You'll check your own account during the session.",
      },
      {
        icon: null,
        headline: "Spot what can be faked.",
        description:
          "Cloned voices, fake video and convincing email are now cheap to make. You'll learn how to verify a request before you act on it, and why every family needs a safe word.",
      },
      {
        icon: null,
        headline: "Decide what your AI is allowed to do.",
        description:
          "Read-only versus draft versus act, giving access to only the files a job needs, which approvals to keep, and the boundaries and kill switch to set before you let an agent work on its own.",
      },
      {
        icon: null,
        headline: "Find your own exposure, live.",
        description:
          "Run a breach check on your email, then use prompts that make your AI list what it already knows about you, tell you how you'd most likely be breached, and flag any password or key you've handed it.",
      },
      {
        icon: null,
        headline: "Clean up what your AI can reach.",
        description:
          "The old tax return in a folder, the inbox full of account numbers: if your AI is connected, all of it is in play. You'll leave knowing what to delete, move or redact first.",
      },
      {
        icon: null,
        headline: "Run a private model on your own computer.",
        description:
          "A step-by-step walkthrough of downloading an open-source model that works offline, why you might want one, and why chats on the big services are not treated like private conversations.",
      },
    ],
    inclusions: [
      { title: "Two Hours Live With Kate Marshall" },
      { title: "Full Recording Access For Life" },
      { title: "The Slides" },
      { title: "The AI Security Kit: Step-by-Step Settings For Each Tool" },
    ],
    audience: {
      heading: "Who this is for",
      forItems: [
        "You use ChatGPT, Claude, Gemini or another AI tool for work or at home.",
        "You run a business or manage a team and want clear rules, not a lecture.",
        "You're not technical and want to know what to turn on, what to turn off and what to never share.",
      ],
      notForItems: [
        "Security teams and IT admins. This is a plain-language session, not a deep technical one.",
        "Anyone who needs help with incident response, digital forensics or industrial control systems.",
      ],
    },
    instructors: [
      {
        name: "Kate Marshall",
        title: "Founder, TheGr.ai · Fractional Chief AI Officer",
        headshotUrl: "/images/instructors/kate-marshall-800.webp",
        bio: "Kate Marshall is the founder of TheGr.ai (Governance and Responsible AI), where she serves as a fractional Chief AI Officer and helps organizations adopt AI with governance and security built in from the start. Spanning two decades in cybersecurity training, she has built practical courses for both technical and non-technical audiences. Today, she focuses on training people to manage AI agents safely and effectively alongside their teams. She is the author of AI at Work and an instructor for the SHRM AI+HI Specialty Credential.",
        // All four come from Kate's approved bio; add nothing she hasn't said.
        accomplishments: [
          "18 years building cybersecurity training",
          "Fractional Chief AI Officer, TheGr.ai",
          "Author of AI at Work",
          "Instructor, SHRM AI+HI Specialty Credential",
        ],
        linkedinUrl: "https://www.linkedin.com/in/kate-b-marshall/",
        displayOrder: 0,
      },
    ],
    faqItems: [
      {
        question: "Do I need to be technical?",
        answer:
          "No. Kate teaches this in plain language for people who use AI, not people who run security. If you can open your account settings, you can do everything in the session.",
      },
      {
        question: "Which AI tools does it cover?",
        answer:
          "All the major ones. The session is vendor agnostic, and the kit you get afterward has step-by-step instructions for each tool so you can make the same changes in whichever one you use.",
      },
      {
        question: "Do I need an AI account to attend?",
        answer:
          "It helps. You'll check your own settings and run a few prompts during the session, so have the tool you use open on your computer.",
      },
      {
        question: "Is this for security professionals?",
        answer:
          "No. It's built for everyone else: business owners, managers and individual users. If you need help with incident response, forensics or industrial systems, this isn't the right session.",
      },
      {
        question: "Is it live? Will there be a recording?",
        answer:
          "It's a two-hour live session on Zoom with no break. Every attendee gets the recording and the slides afterward.",
      },
      {
        question: "What's the refund policy?",
        answer:
          "If you attend and aren't satisfied, email us within 24 hours of the workshop ending and we'll issue a full refund, no questions asked.",
      },
    ],
    brandLogos: [
      { imageUrl: "/images/client-logos/espn.webp", altText: "ESPN", linkUrl: null },
      { imageUrl: "/images/client-logos/jetblue.webp", altText: "JetBlue", linkUrl: null },
      { imageUrl: "/images/client-logos/chase.webp", altText: "Chase", linkUrl: null },
      { imageUrl: "/images/client-logos/tmobile-480.webp", altText: "T-Mobile", linkUrl: null },
      { imageUrl: "/images/client-logos/pbs.svg", altText: "PBS", linkUrl: null },
      { imageUrl: "/images/client-logos/forbes-2.webp", altText: "Forbes", linkUrl: null },
      { imageUrl: "/images/client-logos/nasdaq-480.webp", altText: "Nasdaq", linkUrl: null },
      { imageUrl: "/images/client-logos/amc-networks-480.webp", altText: "AMC Networks", linkUrl: null },
      { imageUrl: "/images/client-logos/asurion-800.webp", altText: "Asurion", linkUrl: null },
      { imageUrl: "/images/client-logos/hanes-480.webp", altText: "Hanes", linkUrl: null },
      { imageUrl: "/images/client-logos/salesforce.svg", altText: "Salesforce", linkUrl: null },
      { imageUrl: "/images/client-logos/paypal.webp", altText: "PayPal", linkUrl: null },
    ],
    showThingsYoullBuild: false,
    stickyBarTitle: "AI Privacy & Security Masterclass",
    learnHeadlineSuffix: "the AI Privacy & Security Masterclass",
  },
};

/**
 * Returns the static content entry for a given series slug.
 * Returns null if the slug is absent, null, or not found in the config.
 * Callers should fall back gracefully (render section as empty/hidden) when null.
 */
export function getWorkshopContent(seriesSlug: string | null | undefined): WorkshopContentEntry | null {
  if (!seriesSlug) return null;
  return workshopContent[seriesSlug] ?? null;
}

export default workshopContent;
